import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { USER_PROFILE_FIELDS, USERS_TABLE } from "@/lib/userAccess";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const withTimeout = (promise, ms) => {
      return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
      ]);
    };

    const loadUserProfile = async (authUserId) => {
      try {
        const { data: userData } = await withTimeout(
          supabase
            .from(USERS_TABLE)
            .select(USER_PROFILE_FIELDS)
            .eq('id', authUserId)
            .single(),
          5000
        );
        if (!isMounted) return;
        if (userData) setUser(userData);
      } catch (e) {
        // No bloquear la UI si el perfil tarda o falla
        console.warn('No se pudo cargar perfil users en tiempo:', e?.message || e);
      }
    };

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        // Liberar UI con el usuario de Auth inmediatamente
        setUser(session?.user || null);
        // Cargar perfil de users en segundo plano (si hay sesión)
        if (session?.user?.id) loadUserProfile(session.user.id);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;
      // Actualizar inmediatamente con Auth
      setUser(session?.user || null);
      // Sincronizar perfil después
      if (session?.user?.id) loadUserProfile(session.user.id);
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  const login = async (username, password) => {
    try {
      const normalizedUsername = username.trim().toLowerCase();

      // Primero autenticación en Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedUsername,
        password: password,
      });
      if (error) throw error;

      // Luego cargar datos de usuario por id (fuente de verdad)
      const authUserId = data?.user?.id;
      if (!authUserId) return { success: false, error: 'No se obtuvo ID de usuario' };

      let { data: userData, error: userError } = await supabase
        .from(USERS_TABLE)
        .select(USER_PROFILE_FIELDS)
        .eq('id', authUserId)
        .single();
      if (userError || !userData) {
        // Fallback: buscar por username si no encontramos por id (caso tablas desincronizadas)
        const { data: byEmailUser, error: byEmailError } = await supabase
          .from(USERS_TABLE)
          .select(USER_PROFILE_FIELDS)
          .ilike('username', normalizedUsername)
          .single();
        if (byEmailError || !byEmailUser) {
          return { success: false, error: 'Usuario no encontrado en tabla users' };
        }
        userData = byEmailUser;
      }

      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Error logging in:', error);
      return { success: false, error: error?.message || 'Error desconocido' };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;

    switch (user.role) {
      case "admin":
        return true;
      case "vendedor":
        return !["entrada", "salida", "productos_gestion", "configuracion"].includes(permission);
      case "cliente":
        return ["productos_vista", "clientes_vista"].includes(permission);
      default:
        return false;
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
